
import React, { useRef, useEffect, useState } from 'react';
import { Play, ArrowRight } from 'lucide-react';

const Demo = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [videoModal, setVideoModal] = useState(false);
  
  useEffect(() => {
    if (!sectionRef.current) return;
    
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );
    
    const elements = sectionRef.current.querySelectorAll('.reveal');
    elements.forEach(el => observer.observe(el));
    
    return () => {
      elements.forEach(el => observer.unobserve(el));
    };
  }, []);

  return (
    <section
      id="demo"
      ref={sectionRef}
      className="py-24 bg-gray-900 text-white relative overflow-hidden"
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 data-grid opacity-10"></div>
      <div className="absolute top-0 left-0 w-full h-40 bg-gradient-to-b from-black/20 to-transparent"></div>
      <div className="absolute bottom-0 left-0 w-full h-40 bg-gradient-to-t from-black/20 to-transparent"></div>
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <div className="inline-block px-3 py-1 mb-6 bg-blue-500/20 text-blue-400 rounded-full text-sm font-medium reveal opacity-0 transition-all duration-700 translate-y-8">
            See It In Action
          </div>
          <h2 className="heading-lg mb-6 reveal opacity-0 transition-all duration-700 translate-y-8" style={{ transitionDelay: '100ms' }}>
            Try Portco AI Today
          </h2>
          <p className="text-gray-300 text-lg reveal opacity-0 transition-all duration-700 translate-y-8" style={{ transitionDelay: '200ms' }}>
            Ready to see how Portco AI can transform your investment analytics?
          </p>
        </div>
        
        {/* Dashboard options */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-8 border border-gray-700 hover-lift reveal opacity-0 transition-all duration-700 translate-y-8" style={{ transitionDelay: '400ms' }}>
            <h3 className="heading-sm mb-4">Schedule a Live Demo</h3>
            <p className="text-gray-300 mb-6">
              Get a personalized walkthrough of Portco AI with one of our product specialists. See how our platform can be tailored to your specific needs.
            </p>
            <a
              href="#contact"
              className="bg-white text-blue-600 hover:bg-gray-100 transition-colors px-6 py-3 rounded-md font-medium inline-flex items-center"
            >
              <span>Book a Demo</span>
              <ArrowRight className="ml-2 w-4 h-4" />
            </a>
          </div>
          
          <div className="bg-blue-600/20 backdrop-blur-sm rounded-xl p-8 border border-blue-500/20 hover-lift reveal opacity-0 transition-all duration-700 translate-y-8" style={{ transitionDelay: '500ms' }}>
            <h3 className="heading-sm mb-4">Try a Free Sample Analysis</h3>
            <p className="text-gray-300 mb-6">
              Submit one of your fund reports and we'll show you what Portco AI can extract and analyze, completely free of charge.
            </p>
            <a
              href="#contact"
              className="bg-blue-600 text-white hover:bg-blue-700 transition-colors px-6 py-3 rounded-md font-medium inline-flex items-center"
            >
              <span>Get Free Analysis</span>
              <ArrowRight className="ml-2 w-4 h-4" />
            </a>
          </div>
        </div>
      </div>
      
      {/* Video Modal */}
      {videoModal && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4">
          <div className="relative w-full max-w-4xl">
            <button 
              className="absolute -top-12 right-0 text-white hover:text-blue-400 transition-colors"
              onClick={() => setVideoModal(false)}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <div className="aspect-video bg-black rounded-lg overflow-hidden">
              <iframe
                width="100%"
                height="100%"
                src="https://www.youtube.com/embed/dQw4w9WgXcQ"
                title="Portco AI Demo"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Demo;
